export const REP_READ_ONLY_INTENTS = [
  'wallet-balance',
  'my-bookings',
  'open-matches',
  'subscription-status',
  'pending-actions',
  'partner-overview',
  'search-content'
];

export const isRepReadOnlyIntent = intent => REP_READ_ONLY_INTENTS.includes(intent);

const formatMoney = (cents, locale) => new Intl.NumberFormat(locale || 'it', {
  style: 'currency',
  currency: 'EUR'
}).format(Number(cents || 0) / 100);

const formatDateTime = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale || 'it', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};
const cleanSport = value => String(value || '')
  .replace(/^sport[_-]/i, '')
  .replace(/^./, character => character.toUpperCase());

const compact = values => values.filter(Boolean).join(' · ');

const itemViews = (tool, data, locale) => {
  const items = Array.isArray(data.items) ? data.items : [];
  if (tool === 'player.upcoming_bookings') {
    return items.map(item => ({
      key: `booking-${item.bookingId}`,
      title: item.partnerName,
      subtitle: compact([formatDateTime(item.startDatetime, locale), (item.fieldNames || []).join(', '), (item.sports || []).map(cleanSport).join(', ')]),
      route: item.route
    }));
  }
  if (tool === 'player.open_matches' || tool === 'partner.open_matches') {
    return items.map(item => ({
      key: `match-${item.publicId}`,
      title: compact([cleanSport(item.sport), item.partnerName || item.fieldName]),
      subtitle: compact([
        formatDateTime(item.startDatetime, locale),
        `${item.playersJoined}/${item.playersRequired}`,
        item.minLevel !== null && item.minLevel !== undefined && item.maxLevel !== null && item.maxLevel !== undefined ? `${item.minLevel}–${item.maxLevel}` : null,
        item.participantPriceCents !== null && item.participantPriceCents !== undefined ? formatMoney(item.participantPriceCents, locale) : null
      ]),
      route: item.route
    }));
  }
  if (tool === 'player.subscriptions') {
    return items.map(item => ({
      key: `subscription-${item.id}`,
      title: item.channelName || item.partnerName || item.kind,
      subtitle: compact([item.active ? 'REP_TOOL_ACTIVE' : 'REP_TOOL_EXPIRED', formatDateTime(item.expiryDatetime, locale), item.autoRenew ? 'REP_TOOL_AUTO_RENEW' : null]),
      translateSubtitleParts: true,
      route: item.route
    }));
  }
  if (tool === 'player.content_search' || tool === 'partner.content_search') {
    return items.map(item => ({
      key: `content-${item.contentType}-${item.contentId}`,
      title: item.title,
      subtitle: compact([formatDateTime(item.startDatetime, locale), cleanSport(item.sport), item.fieldName, item.partnerName]),
      contentResult: item
    }));
  }
  return [];
};

export const getRepToolView = (result, locale = 'it') => {
  if (!result || result.readOnly !== true || !result.tool || !result.data) return null;
  const {tool, data} = result;
  const base = {
    titleKey: (result.presentation && result.presentation.titleKey) || 'REP_TOOL_RESULT_TITLE',
    helpKey: 'REP_TOOL_RESULT_HELP',
    route: result.presentation && result.presentation.route,
    actionKey: 'REP_TOOL_OPEN_SECTION',
    emptyKey: 'REP_TOOL_NO_ITEMS',
    stats: [],
    items: itemViews(tool, data, locale)
  };

  if (tool.endsWith('wallet_summary')) {
    return {...base, helpKey: 'REP_TOOL_WALLET_HELP', stats: [
      {labelKey: 'REP_TOOL_AVAILABLE_CREDIT', value: formatMoney(data.availableCents, locale)},
      {labelKey: 'REP_TOOL_TOTAL_CREDIT', value: formatMoney(data.walletCents, locale)},
      {labelKey: 'REP_TOOL_BLOCKED_CREDIT', value: formatMoney(data.heldCents, locale)}
    ]};
  }
  if (tool === 'partner.today_summary') {
    return {...base, helpKey: 'REP_TOOL_TODAY_HELP', stats: [
      {labelKey: 'REP_TOOL_BOOKINGS', value: data.bookings || 0},
      {labelKey: 'REP_TOOL_BOOKED_SLOTS', value: data.bookedSlots || 0},
      {labelKey: 'REP_TOOL_ACTIVE_FIELDS', value: data.activeFields || 0},
      {labelKey: 'REP_TOOL_OCCUPIED_MINUTES', value: data.occupiedMinutes || 0}
    ]};
  }
  if (tool.endsWith('pending_actions')) {
    const stats = tool.startsWith('player.') ? [
      {labelKey: 'REP_TOOL_INVITATIONS', value: data.openMatchInvitations || 0},
      {labelKey: 'REP_TOOL_RESULTS_TO_CONFIRM', value: data.resultConfirmations || 0},
      {labelKey: 'REP_TOOL_RESULTS_TO_SUBMIT', value: data.resultsToSubmit || 0},
      {labelKey: 'REP_TOOL_UNREAD_NOTIFICATIONS', value: data.unreadNotifications || 0}
    ] : [
      {labelKey: 'REP_TOOL_RENEWALS', value: data.affiliationRenewals || 0},
      {labelKey: 'REP_TOOL_RANKING_REQUESTS', value: data.rankingVerifications || 0},
      {labelKey: 'REP_TOOL_EXPIRING_AFFILIATIONS', value: data.affiliationsExpiringWithin30Days || 0},
      {labelKey: 'REP_TOOL_UNREAD_NOTIFICATIONS', value: data.unreadNotifications || 0}
    ];
    return {...base, helpKey: data.total ? 'REP_TOOL_PENDING_HELP' : 'REP_TOOL_NOTHING_PENDING', stats};
  }
  if (tool.includes('upcoming_bookings')) return {...base, helpKey: 'REP_TOOL_BOOKINGS_HELP', emptyKey: 'REP_TOOL_NO_BOOKINGS'};
  if (tool.includes('open_matches')) return {...base, helpKey: 'REP_TOOL_OPEN_MATCHES_HELP', emptyKey: 'REP_TOOL_NO_OPEN_MATCHES'};
  if (tool.includes('subscriptions')) return {...base, helpKey: 'REP_TOOL_SUBSCRIPTIONS_HELP', emptyKey: 'REP_TOOL_NO_SUBSCRIPTIONS'};
  if (tool.includes('content_search')) return {
    ...base,
    helpKey: 'REP_TOOL_CONTENT_SEARCH_HELP',
    emptyKey: 'REP_TOOL_NO_CONTENT',
    actionKey: 'REP_TOOL_OPEN_REPLAYERTV'
  };
  return base;
};
